import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../lib/api';

const STATUS_PILL = {
  Scripted:   'pill-scripted',
  Filming:    'pill-filming',
  Filmed:     'pill-filmed',
  Editing:    'pill-editing',
  Qual:       'pill-qual',
  Approved:   'pill-approved',
  Posted:     'pill-posted',
  Incomplete: 'pill-incomplete',
};

// Default a shoot_day picker to the next occurrence of `targetDow` (0=Sun) from today.
// e.g. "Pick a Thursday" → returns YYYY-MM-DD of the next Thursday on or after today.
function nextOccurrence(targetDow) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = (targetDow - today.getDay() + 7) % 7;
  const d = new Date(today);
  d.setDate(today.getDate() + (diff === 0 ? 7 : diff));
  return d.toISOString().slice(0, 10);
}

function dateInputToISO(s) { return s || null; }

export default function SchedulerPanel({ record, allTalent = [], onClose, onProjectUpdated }) {
  const [segments,  setSegments]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [notes,     setNotes]     = useState(record.notes || '');
  const [savingNotes, setSavingNotes] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  // Save button: fields auto-save on blur/change today, so the button's job is
  // to flush any focused field (force blur) and confirm visually. Belt-and-
  // suspenders for ops who don't trust silent auto-save.
  const handleSave = useCallback(() => {
    if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1500);
  }, []);

  // Fetch segments + (lazily) the talent list if parent didn't pass one
  const [talentList, setTalentList] = useState(allTalent);

  const loadSegments = useCallback(async () => {
    try {
      const r = await api.get(`/api/shoot-segments/by-project/${record.id}`);
      setSegments(r.data);
    } catch {
      /* silent — empty state is fine */
    } finally { setLoading(false); }
  }, [record.id]);

  useEffect(() => { loadSegments(); }, [loadSegments]);
  useEffect(() => {
    if (talentList.length === 0) {
      api.get('/api/talent').then(r => setTalentList(r.data)).catch(() => {});
    }
  }, [talentList.length]);

  // ── Mutations ────────────────────────────────────────────────────────────
  const saveNotes = useCallback(async () => {
    if (notes === (record.notes || '')) return;
    setSavingNotes(true);
    try {
      const r = await api.patch(`/api/live-production/${record.id}`, { notes });
      onProjectUpdated?.(r.data);
    } catch {
      alert('Failed to save notes.');
    } finally { setSavingNotes(false); }
  }, [notes, record.id, record.notes, onProjectUpdated]);

  const addSegment = useCallback(async () => {
    const label = `Cam ${segments.length + 1}`;
    try {
      const r = await api.post(`/api/shoot-segments/${record.id}`, {
        segmentLabel: label,
        sortOrder:    segments.length,
      });
      setSegments(prev => [...prev, r.data]);
    } catch {
      alert('Failed to add segment.');
    }
  }, [segments.length, record.id]);

  const patchSegment = useCallback(async (segmentId, patch) => {
    // Optimistic update
    setSegments(prev => prev.map(s => s.id === segmentId ? { ...s, ...patch, talents: patch.talents ? patch.talents : s.talents } : s));
    try {
      const r = await api.patch(`/api/shoot-segments/${segmentId}`, patch);
      setSegments(prev => prev.map(s => s.id === segmentId ? r.data : s));
      // Status sync may have flipped the parent VC status — refresh
      if (patch.shootStatus !== undefined) {
        try {
          const p = await api.get(`/api/live-production/${record.id}`);
          onProjectUpdated?.(p.data);
        } catch {}
      }
    } catch {
      alert('Failed to update segment.');
      loadSegments(); // rollback to server state
    }
  }, [record.id, loadSegments, onProjectUpdated]);

  const deleteSegment = useCallback(async (segmentId) => {
    if (!window.confirm('Delete this segment?')) return;
    setSegments(prev => prev.filter(s => s.id !== segmentId));
    try {
      await api.delete(`/api/shoot-segments/${segmentId}`);
      try {
        const p = await api.get(`/api/live-production/${record.id}`);
        onProjectUpdated?.(p.data);
      } catch {}
    } catch {
      alert('Failed to delete segment.');
      loadSegments();
    }
  }, [record.id, loadSegments, onProjectUpdated]);

  const archiveProject = useCallback(async () => {
    if (!window.confirm('All segments are filmed. Remove this project from the active shoot schedule?')) return;
    setArchiving(true);
    try {
      await api.post(`/api/shoot-segments/${record.id}/archive`, { archived: true });
      onProjectUpdated?.({ ...record, isSchedulerArchived: true });
      onClose();
    } catch {
      alert('Failed to archive.');
    } finally { setArchiving(false); }
  }, [record, onProjectUpdated, onClose]);

  // ── Derived state ────────────────────────────────────────────────────────
  const filmedCount = useMemo(() => segments.filter(s => s.shootStatus === 'filmed').length, [segments]);
  const allFilmed   = segments.length > 0 && filmedCount === segments.length;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="vc-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 920 }}>

        {/* Header */}
        <div className="vc-header">
          <div style={{ minWidth: 0 }}>
            <div className="vc-label">Shoot Scheduler</div>
            <div className="vc-title">{record.script?.scriptName || record.notes || '(Untitled project)'}{record.episodeNumber ? <span style={{ marginLeft: 8, fontSize: 12, color: 'rgba(139,109,255,0.85)' }}>Ep {record.episodeNumber}</span> : null}</div>
            <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span className={`pill ${STATUS_PILL[record.status] || ''}`}>{record.status}</span>
              <span style={{ fontSize: 11, color: 'var(--tx-muted)' }}>{record.client?.name}</span>
              <span style={{ fontSize: 11, color: 'var(--tx-secondary)' }}>
                {segments.length === 0 ? 'No segments yet' : `${filmedCount} of ${segments.length} filmed`}
              </span>
            </div>
          </div>
          <button className="vc-close" onClick={onClose}>✕</button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 28px 28px', overflowY: 'auto', flex: 1, minHeight: 0 }}>

          {/* Segments list */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--tx-muted)' }}>
                Shoot Segments
              </div>
              <button className="btn-ghost" style={{ fontSize: 12, padding: '4px 12px' }} onClick={addSegment}>+ Add Segment</button>
            </div>

            {loading ? (
              <div style={{ fontSize: 12, color: 'var(--tx-faint)', padding: '12px 0' }}>Loading…</div>
            ) : segments.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--tx-faint)', padding: '20px 0', textAlign: 'center', border: '1px dashed var(--border)', borderRadius: 'var(--radius-sm)' }}>
                No segments yet. Add one to start scheduling shoot setups.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {segments.map(seg => (
                  <SegmentRow
                    key={seg.id}
                    segment={seg}
                    talentList={talentList}
                    onChange={patch => patchSegment(seg.id, patch)}
                    onDelete={() => deleteSegment(seg.id)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Project notes (same field as the Video Card's notes) */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--tx-muted)', marginBottom: 6 }}>
              Notes for Filming
              {savingNotes && <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--tx-faint)', textTransform: 'none', letterSpacing: 0 }}>saving…</span>}
            </div>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              onBlur={saveNotes}
              placeholder="Project-level notes — synced with the Production page."
              rows={3}
              style={{
                width: '100%', padding: '8px 10px', fontSize: 12, fontFamily: 'var(--font)',
                background: 'var(--bg-hover)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)', color: 'var(--tx-primary)', resize: 'vertical', outline: 'none',
              }}
            />
          </div>

          {/* Archive */}
          {allFilmed && !record.isSchedulerArchived && (
            <div style={{ padding: 14, borderRadius: 'var(--radius-sm)', background: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.25)' }}>
              <div style={{ fontSize: 12, color: 'var(--tx-secondary)', marginBottom: 8 }}>
                All segments are filmed. Remove this project from the active shoot schedule?
              </div>
              <button className="btn-primary" onClick={archiveProject} disabled={archiving} style={{ fontSize: 12 }}>
                {archiving ? 'Archiving…' : 'Archive Project'}
              </button>
            </div>
          )}
        </div>

        {/* Footer — Save button. Fields auto-save on blur/change; this button
            flushes any focused input and flashes a "Saved" confirmation so
            users have a visible commit affordance. */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12,
          padding: '12px 28px', borderTop: '1px solid var(--border)', background: 'var(--bg-card)',
        }}>
          {savedFlash && (
            <span style={{ fontSize: 12, color: 'var(--success, #4ade80)', fontWeight: 600 }}>
              Saved ✓
            </span>
          )}
          <button className="btn-ghost" style={{ fontSize: 12, padding: '6px 14px' }} onClick={onClose}>
            Close
          </button>
          <button className="btn-primary" style={{ fontSize: 12, padding: '6px 16px' }} onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Segment row ─────────────────────────────────────────────────────────────
function SegmentRow({ segment, talentList, onChange, onDelete }) {
  const [label,   setLabel]   = useState(segment.segmentLabel);
  const [setting, setSetting] = useState(segment.setting || '');

  // Keep local fields in sync if the parent re-renders with new server data
  useEffect(() => { setLabel(segment.segmentLabel);   }, [segment.segmentLabel]);
  useEffect(() => { setSetting(segment.setting || ''); }, [segment.setting]);

  const shootDayStr = segment.shootDay ? segment.shootDay.slice(0, 10) : '';
  const isFilmed    = segment.shootStatus === 'filmed';

  // Availability warning: if a talent is assigned AND a day is set, check the row.
  const [availWarnings, setAvailWarnings] = useState({});
  useEffect(() => {
    if (!shootDayStr) { setAvailWarnings({}); return; }
    const assigned = (segment.talents || []).filter(t => t.talentId);
    if (assigned.length === 0) { setAvailWarnings({}); return; }
    let cancelled = false;
    (async () => {
      const out = {};
      await Promise.all(assigned.map(async (t) => {
        try {
          const r = await api.get(`/api/shoot-segments/availability-check?talentId=${t.talentId}&date=${shootDayStr}`);
          if (!r.data.available) out[t.talentId] = true;
        } catch { /* ignore */ }
      }));
      if (!cancelled) setAvailWarnings(out);
    })();
    return () => { cancelled = true; };
  }, [shootDayStr, segment.talents]);

  const addTalent = (talentId) => {
    if (!talentId) return;
    const exists = (segment.talents || []).some(t => t.talentId === talentId);
    if (exists) return;
    const next = [...(segment.talents || []), { talentId }];
    onChange({ talents: next.map(t => ({ talentId: t.talentId || null, talentDisplayName: t.talentDisplayName || null })) });
  };
  const removeTalent = (idx) => {
    const next = (segment.talents || []).filter((_, i) => i !== idx);
    onChange({ talents: next.map(t => ({ talentId: t.talentId || null, talentDisplayName: t.talentDisplayName || null })) });
  };

  return (
    <div style={{
      padding: 12, borderRadius: 'var(--radius-sm)',
      background: isFilmed ? 'rgba(74,222,128,0.05)' : 'var(--bg-hover)',
      border: '1px solid var(--border)',
      display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 10, alignItems: 'start',
    }}>
      {/* Status checkbox */}
      <button
        onClick={() => onChange({ shootStatus: isFilmed ? 'incomplete' : 'filmed' })}
        title={isFilmed ? 'Mark incomplete' : 'Mark filmed'}
        style={{
          width: 22, height: 22, borderRadius: 4, marginTop: 2,
          background: isFilmed ? '#4ade80' : 'transparent',
          border: `1.5px solid ${isFilmed ? '#4ade80' : 'var(--border)'}`,
          color: '#0a0a0d', fontSize: 13, lineHeight: 1, cursor: 'pointer', fontFamily: 'var(--font)',
        }}
      >{isFilmed ? '✓' : ''}</button>

      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Label + delete row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            value={label}
            onChange={e => setLabel(e.target.value)}
            onBlur={() => { if (label !== segment.segmentLabel) onChange({ segmentLabel: label }); }}
            placeholder="Segment label (e.g. Cam 1)"
            style={{
              flex: 1, padding: '4px 8px', fontSize: 13, fontWeight: 600,
              background: 'transparent', border: '1px solid transparent', borderRadius: 4,
              color: 'var(--tx-primary)', outline: 'none', fontFamily: 'var(--font)',
              textDecoration: isFilmed ? 'line-through' : 'none', opacity: isFilmed ? 0.7 : 1,
            }}
            onFocus={e => e.target.style.border = '1px solid var(--border)'}
          />
        </div>

        {/* Talent pills */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          {(segment.talents || []).map((t, idx) => (
            <span key={t.id || idx} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '3px 8px 3px 10px', fontSize: 11, borderRadius: 'var(--radius-pill)',
              background: 'var(--bg-app)', border: '1px solid var(--border)',
              color: 'var(--tx-secondary)',
            }}>
              {availWarnings[t.talentId] && <span title="Talent not available on this day" style={{ color: 'var(--s-incomplete-t)' }}>⚠</span>}
              {t.talent?.name || t.talentDisplayName || '(unknown)'}
              <button onClick={() => removeTalent(idx)} style={{ background: 'none', border: 'none', color: 'var(--tx-faint)', cursor: 'pointer', fontSize: 12, padding: 0, lineHeight: 1 }}>×</button>
            </span>
          ))}
          <select
            value=""
            onChange={e => { addTalent(e.target.value); e.target.value = ''; }}
            style={{
              padding: '3px 8px', fontSize: 11, background: 'transparent',
              border: '1px dashed var(--border)', borderRadius: 'var(--radius-pill)',
              color: 'var(--tx-faint)', cursor: 'pointer', fontFamily: 'var(--font)', outline: 'none',
            }}
          >
            <option value="">+ Add talent</option>
            {talentList
              .filter(t => !(segment.talents || []).some(st => st.talentId === t.id))
              .map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>

        {/* Day + setting */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            type="date"
            value={shootDayStr}
            onChange={e => onChange({ shootDay: dateInputToISO(e.target.value) })}
            style={{
              padding: '4px 8px', fontSize: 12, background: 'var(--bg-app)',
              border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
              color: 'var(--tx-primary)', fontFamily: 'var(--font)', outline: 'none',
            }}
          />
          <input
            value={setting}
            onChange={e => setSetting(e.target.value)}
            onBlur={() => { if (setting !== (segment.setting || '')) onChange({ setting }); }}
            placeholder="Setting (e.g. news desk)"
            style={{
              flex: 1, minWidth: 160, padding: '4px 8px', fontSize: 12,
              background: 'var(--bg-app)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)', color: 'var(--tx-primary)',
              fontFamily: 'var(--font)', outline: 'none',
            }}
          />
        </div>
      </div>

      {/* Delete */}
      <button
        onClick={onDelete}
        title="Delete segment"
        className="btn-ghost"
        style={{ fontSize: 14, padding: '4px 8px', color: 'var(--tx-faint)' }}
      >🗑</button>
    </div>
  );
}
